import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowRight, Loader2, ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import { useFeaturedVendors } from '../../services/vendorService';
import useUserLocation from '../../hooks/useUserLocation';
import VendorPerformanceBadge from '../client/VendorPerformanceBadge';

const FeaturedVendors = () => {
  const { userLocation, loading: locationLoading } = useUserLocation();
  
  // Build params for location-based filtering using useMemo to avoid unnecessary re-renders
  const vendorParams = useMemo(() => {
    const params = {
      per_page: 6
    };
    
    if (userLocation) {
      // Prefer ISO country code when available (US vs "United States" / "USA")
      const countryParam = userLocation.countryCode || userLocation.country || null;

      // Priority: Use latitude/longitude for distance-based sorting (not filtering)
      if (userLocation.latitude && userLocation.longitude) {
        params.latitude = userLocation.latitude;
        params.longitude = userLocation.longitude;
        if (countryParam) {
          params.country = countryParam;
        }
        // Don't send radius - this way backend won't filter by distance, only sort
        params.sort_by = 'distance';
        params.sort_order = 'asc';
      } else if (userLocation.city) {
        params.city = userLocation.city;
        if (countryParam) {
          params.country = countryParam;
        }
      }
      if (!userLocation.latitude && !userLocation.longitude && userLocation.state) {
        params.state = userLocation.state;
      }
      if (countryParam && !params.country) {
        params.country = countryParam;
      }
    } else {
      console.log('FeaturedVendors: No location available yet');
    }
    
    return params;
  }, [userLocation]);
  
  // Only fetch when we have location with coordinates and country
  const locationCountry = userLocation?.countryCode || userLocation?.country;
  const hasCompleteLocation = !!(userLocation?.latitude && userLocation?.longitude && locationCountry);
  const shouldFetch = hasCompleteLocation || (!locationLoading && !userLocation);
  
  // Fetch featured vendors from API
  const { data: vendorsResponse, isLoading, error, refetch, isFetching } = useFeaturedVendors(
    vendorParams,
    { enabled: shouldFetch, retry: 2 }
  );
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Transform API data to Airbnb-style format
  const featuredVendors = vendorsResponse?.data?.data?.map((vendor) => {
    const city = vendor?.city || '';
    const state = vendor?.state || '';
    
    return {
      id: vendor.id,
      title: vendor?.business_name || 'Vendor',
      rating: Number(vendor?.average_rating) || 0,
      performanceScore: Number(vendor?.performance_score) || 0,
      platformBookingsCount: Number(vendor?.platform_bookings_count) || 0,
      price: Number(vendor?.min_price) || 0,
      image: vendor?.image_url || vendor?.user?.profile_image_url || '/api/placeholder/300/200',
      isFeatured: true, // All featured vendors get the badge
      location: vendor ? `${city}, ${state}` : 'Location not available',
      description: vendor?.description || ''
    };
  }) || [];

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Check initial scroll position
  useEffect(() => {
    handleScroll();
    if (scrollRef.current) {
      const resizeObserver = new ResizeObserver(() => handleScroll());
      resizeObserver.observe(scrollRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [featuredVendors.length]);

  return (
    <section className="py-8 bg-white relative overflow-hidden">
      <div className="page-container relative z-10">
        {/* Section Header with Navigation Arrows */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/browse-vendors"
            className="text-xl font-medium text-gray-900 flex items-center hover:text-gray-700 transition-colors group"
          >
            Featured Vendors
            <ArrowRight className="w-5 h-5 ml-2 text-gray-600 group-hover:translate-x-1 transition-transform" />
          </Link>
          
          {/* Navigation Arrows - Top Right */}
          {!isLoading && !error && featuredVendors.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => scroll('left')}
                disabled={!showLeftArrow}
                className={`rounded-full p-1.5 transition-all border ${
                  showLeftArrow
                    ? 'bg-white border-gray-300 hover:border-gray-400 text-gray-700 hover:shadow-md'
                    : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <button
                onClick={() => scroll('right')}
                disabled={!showRightArrow}
                className={`rounded-full p-1.5 transition-all border ${
                  showRightArrow
                    ? 'bg-white border-gray-300 hover:border-gray-400 text-gray-700 hover:shadow-md'
                    : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                aria-label="Scroll right"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="flex items-center space-x-2 text-gray-600">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-lg font-medium">Loading featured vendors...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="text-red-500 text-lg font-medium mb-2">Failed to load featured vendors</div>
              <p className="text-gray-600 mb-3">Please try again later</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="text-sm font-medium text-gray-900 underline"
                disabled={isFetching}
              >
                {isFetching ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          </div>
        ) : featuredVendors.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="text-gray-500 text-lg font-medium mb-2">
                {locationCountry
                  ? `No featured vendors in ${userLocation?.country || locationCountry} yet`
                  : 'No featured vendors available'}
              </div>
              <p className="text-gray-600">
                {locationCountry
                  ? 'Try another location, or check back as more vendors join this market.'
                  : 'Check back later for featured professionals'}
              </p>
            </div>
          </div>
        ) : (
          <div className="relative">

            {/* Horizontal Scrolling Carousel */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {featuredVendors.map((vendor) => (
                <Link
                  key={vendor.id}
                  to={`/vendor/${vendor.id}`}
                  
                  className="flex-shrink-0 w-[calc((100%-5*1rem)/6)] min-w-[180px] group"
                >
                  <div className="relative rounded-2xl overflow-hidden">
                    {/* Image Container */}
                    <div className="relative h-48 bg-gray-200 overflow-hidden rounded-2xl">
                      <img
                        src={vendor.image}
                        alt={vendor.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      
                      {/* Guest Favorite Badge */}
                      {vendor.isFeatured && (
                        <div className="absolute top-3 left-3 bg-gray-900/80 text-white px-2.5 py-1 rounded-md text-xs font-medium">
                          Featured
                        </div>
                      )}

                      <div className="absolute top-3 right-3">
                        <VendorPerformanceBadge
                          score={vendor.performanceScore}
                          bookingsCount={vendor.platformBookingsCount}
                        />
                      </div>

                      {/* Heart Icon */}
                      {/* <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors opacity-0 group-hover:opacity-100"
                        aria-label="Add to favorites"
                      >
                        <Heart className="w-4 h-4 text-gray-700" />
                      </button> */}
                    </div>

                    {/* Card Content */}
                    <div className="mt-3">
                      {/* Title and Rating */}
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-1 flex-1">
                          {vendor.title}
                        </h3>
                        <div className="flex items-center ml-2 flex-shrink-0">
                          <Star className="w-3 h-3 fill-current text-gray-900" />
                          <span className="text-xs font-medium text-gray-900 ml-1">
                            {vendor.rating > 0 ? Number(vendor.rating).toFixed(2) : 'New'}
                          </span>
                        </div>
                      </div>

                      {/* Location */}
                      <p className="text-xs text-gray-500 mb-2 line-clamp-1">
                        {vendor.location}
                      </p>

                      {/* Price */}
                      <div className="flex items-center">
                        <span className="text-sm font-semibold text-gray-900">
                          ${vendor.price > 0 ? Number(vendor.price).toFixed(0) : 'Contact'}
                        </span>
                        <span className="text-xs text-gray-600 ml-1">starting from</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedVendors;
